import { useState, useEffect, useCallback } from 'react'
import { ecomApi, promoApi, shippingApi, type ApiProduct, type ApiOrder, type ApiPromoCode, type ApiShippingZone } from '../utils/api'

interface Props {
  siteId: string | null
  onClose: () => void
}

type Tab = 'products' | 'orders' | 'promos' | 'shipping'
type OrderStatus = 'ALL' | 'PENDING' | 'CONFIRMED' | 'PROCESSING' | 'SHIPPED' | 'DELIVERED' | 'CANCELLED'

const ORDER_STATUS_LABELS: Record<string, { label: string; color: string }> = {
  PENDING:    { label: 'Новый',         color: '#f59e0b' },
  CONFIRMED:  { label: 'Подтверждён',   color: '#3b82f6' },
  PROCESSING: { label: 'В обработке',   color: '#8b5cf6' },
  SHIPPED:    { label: 'Отправлен',     color: '#06b6d4' },
  DELIVERED:  { label: 'Доставлен',     color: '#22c55e' },
  CANCELLED:  { label: 'Отменён',       color: '#ef4444' },
  REFUNDED:   { label: 'Возврат',       color: '#64748b' },
}

interface EditPromo {
  id?: string
  code: string
  type: 'PERCENT' | 'FIXED'
  value: string
  minOrder: string
  usageLimit: string
  expiresAt: string
}

function emptyPromo(): EditPromo {
  return { code: '', type: 'PERCENT', value: '', minOrder: '', usageLimit: '', expiresAt: '' }
}

export function ShopPanel({ siteId, onClose }: Props) {
  const [tab, setTab] = useState<Tab>('products')
  const [products, setProducts] = useState<ApiProduct[]>([])
  const [orders, setOrders] = useState<ApiOrder[]>([])
  const [promos, setPromos] = useState<ApiPromoCode[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [orderFilter, setOrderFilter] = useState<OrderStatus>('ALL')
  const [selectedOrder, setSelectedOrder] = useState<ApiOrder | null>(null)
  const [editProduct, setEditProduct] = useState<Partial<ApiProduct> | null>(null)
  const [editPromo, setEditPromo] = useState<EditPromo | null>(null)
  const [shippingZones, setShippingZones] = useState<ApiShippingZone[]>([])
  const [addingZone, setAddingZone] = useState(false)
  const [newZone, setNewZone] = useState({ name: '', regions: '', flatRate: '0', freeAbove: '' })
  const [saving, setSaving] = useState(false)

  const loadProducts = useCallback(async () => {
    if (!siteId) return
    setLoading(true)
    setError('')
    try {
      const data = await ecomApi.listProducts(siteId)
      setProducts(data)
    } catch (e) { setError(String(e)) }
    finally { setLoading(false) }
  }, [siteId])

  const loadOrders = useCallback(async () => {
    if (!siteId) return
    setLoading(true)
    setError('')
    try {
      const data = await ecomApi.listOrders(siteId, orderFilter === 'ALL' ? undefined : orderFilter)
      setOrders(data)
    } catch (e) { setError(String(e)) }
    finally { setLoading(false) }
  }, [siteId, orderFilter])

  const loadPromos = useCallback(async () => {
    if (!siteId) return
    setLoading(true)
    setError('')
    try {
      const data = await promoApi.list(siteId)
      setPromos(data)
    } catch (e) { setError(String(e)) }
    finally { setLoading(false) }
  }, [siteId])

  const loadShipping = useCallback(async () => {
    if (!siteId) return
    setLoading(true)
    try {
      const data = await shippingApi.list(siteId)
      setShippingZones(data)
    } catch (e) { setError(String(e)) }
    finally { setLoading(false) }
  }, [siteId])

  useEffect(() => {
    if (tab === 'products') loadProducts()
    else if (tab === 'orders') loadOrders()
    else if (tab === 'promos') loadPromos()
    else loadShipping()
  }, [tab, loadProducts, loadOrders, loadPromos, loadShipping])

  function exportProductsCsv() {
    const headers = ['name', 'price', 'compareAt', 'stock', 'category', 'description', 'isDigital', 'published']
    const rows = products.map(p => [
      p.name, p.price, p.compareAt ?? '', p.stock ?? '', p.category ?? '',
      p.description ?? '', p.isDigital ? 'yes' : 'no', p.published ? 'yes' : 'no',
    ])
    const csv = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n')
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = 'products.csv'
    a.click()
    URL.revokeObjectURL(a.href)
  }

  function exportOrdersCsv() {
    const headers = ['number', 'status', 'customerName', 'customerEmail', 'customerPhone', 'total', 'createdAt']
    const rows = orders.map(o => [
      o.number, o.status, o.customerName ?? '', o.customerEmail ?? '',
      o.customerPhone ?? '', o.total, new Date(o.createdAt).toLocaleDateString('ru'),
    ])
    const csv = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n')
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = 'orders.csv'
    a.click()
    URL.revokeObjectURL(a.href)
  }

  async function saveProduct() {
    if (!siteId || !editProduct) return
    setSaving(true)
    try {
      if (editProduct.id) {
        const updated = await ecomApi.updateProduct(siteId, editProduct.id, editProduct as Parameters<typeof ecomApi.updateProduct>[2])
        setProducts(p => p.map(x => x.id === updated.id ? updated : x))
      } else {
        const created = await ecomApi.createProduct(siteId, { ...editProduct, price: Number(editProduct.price ?? 0) } as Parameters<typeof ecomApi.createProduct>[1])
        setProducts(p => [created, ...p])
      }
      setEditProduct(null)
    } catch (e) { setError(String(e)) }
    finally { setSaving(false) }
  }

  async function deleteProduct(id: string) {
    if (!siteId || !confirm('Удалить товар?')) return
    try {
      await ecomApi.deleteProduct(siteId, id)
      setProducts(p => p.filter(x => x.id !== id))
    } catch (e) { setError(String(e)) }
  }

  async function savePromo() {
    if (!siteId || !editPromo) return
    setSaving(true)
    try {
      const payload = {
        code: editPromo.code,
        type: editPromo.type,
        value: Number(editPromo.value),
        minOrder: editPromo.minOrder ? Number(editPromo.minOrder) : undefined,
        usageLimit: editPromo.usageLimit ? Number(editPromo.usageLimit) : undefined,
        expiresAt: editPromo.expiresAt || undefined,
      }
      if (editPromo.id) {
        const updated = await promoApi.update(siteId, editPromo.id, { usageLimit: payload.usageLimit, expiresAt: payload.expiresAt })
        setPromos(p => p.map(x => x.id === updated.id ? updated : x))
      } else {
        const created = await promoApi.create(siteId, payload)
        setPromos(p => [created, ...p])
      }
      setEditPromo(null)
    } catch (e) { setError(String(e)) }
    finally { setSaving(false) }
  }

  async function togglePromo(id: string, active: boolean) {
    if (!siteId) return
    try {
      const updated = await promoApi.update(siteId, id, { active })
      setPromos(p => p.map(x => x.id === updated.id ? updated : x))
    } catch (e) { setError(String(e)) }
  }

  async function deletePromo(id: string) {
    if (!siteId || !confirm('Удалить промокод?')) return
    try {
      await promoApi.delete(siteId, id)
      setPromos(p => p.filter(x => x.id !== id))
    } catch (e) { setError(String(e)) }
  }

  async function updateStatus(orderId: string, status: string) {
    if (!siteId) return
    try {
      const updated = await ecomApi.updateOrderStatus(siteId, orderId, status)
      setOrders(o => o.map(x => x.id === updated.id ? updated : x))
      setSelectedOrder(updated)
    } catch (e) { setError(String(e)) }
  }

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div style={{ background: 'var(--bg-1)', border: '1px solid var(--border)', borderRadius: '16px', width: '960px', maxWidth: '96vw', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 64px rgba(0,0,0,0.3)' }}>

        {/* Header */}
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
          <div style={{ display: 'flex', gap: '4px', background: 'var(--bg-2)', borderRadius: '8px', padding: '3px' }}>
            {(['products', 'orders', 'promos', 'shipping'] as Tab[]).map(t => {
              const label = t === 'products' ? 'Товары' : t === 'orders' ? 'Заказы' : t === 'promos' ? 'Промокоды' : '🚚 Доставка'
              return (
                <button
                  key={t}
                  onClick={() => { setTab(t); setSelectedOrder(null); setEditProduct(null); setEditPromo(null) }}
                  style={{ padding: '5px 14px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 600, background: tab === t ? 'var(--bg-1)' : 'transparent', color: tab === t ? 'var(--text-primary)' : 'var(--text-muted)' }}
                >
                  {label}
                  {t === 'orders' && orders.filter(o => o.status === 'PENDING').length > 0 && (
                    <span style={{ marginLeft: '5px', background: '#f59e0b', color: '#fff', borderRadius: '10px', padding: '1px 5px', fontSize: '10px', fontWeight: 700 }}>
                      {orders.filter(o => o.status === 'PENDING').length}
                    </span>
                  )}
                </button>
              )
            })}
          </div>
          <div style={{ flex: 1, fontSize: '14px', fontWeight: 700, color: 'var(--text-primary)', paddingLeft: '8px' }}>
            Магазин
          </div>
          {tab === 'products' && (
            <>
              <button
                onClick={exportProductsCsv}
                style={{ padding: '6px 12px', background: 'var(--bg-2)', color: 'var(--text-muted)', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '12px', cursor: 'pointer' }}
              >↓ CSV</button>
              {siteId && (
                <a
                  href={`/api/sites/${siteId}/products/export/woocommerce`}
                  download="products-woocommerce.xml"
                  style={{ padding: '6px 12px', background: 'var(--bg-2)', color: 'var(--text-muted)', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '12px', cursor: 'pointer', textDecoration: 'none' }}
                >↓ WooCommerce</a>
              )}
              {siteId && (
                <a
                  href={`/api/sites/${siteId}/products/export/1c`}
                  download="products-1c.xml"
                  style={{ padding: '6px 12px', background: 'var(--bg-2)', color: 'var(--text-muted)', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '12px', cursor: 'pointer', textDecoration: 'none' }}
                >↓ 1C XML</a>
              )}
              {siteId && (
                <a
                  href={`/api/sites/${siteId}/products/export/google-merchant`}
                  download="google-merchant-feed.xml"
                  style={{ padding: '6px 12px', background: 'var(--bg-2)', color: 'var(--text-muted)', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '12px', cursor: 'pointer', textDecoration: 'none' }}
                >↓ Google Merchant</a>
              )}
              <button
                onClick={() => setEditProduct({ name: '', price: '0', stock: 0, images: [], published: false })}
                style={{ padding: '6px 14px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
              >+ Товар</button>
            </>
          )}
          {tab === 'promos' && (
            <button
              onClick={() => setEditPromo(emptyPromo())}
              style={{ padding: '6px 14px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
            >+ Промокод</button>
          )}
          {tab === 'shipping' && (
            <button
              onClick={() => setAddingZone(true)}
              style={{ padding: '6px 14px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
            >+ Зона</button>
          )}
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '20px', color: 'var(--text-muted)', cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>

        {error && (
          <div style={{ padding: '8px 20px', background: '#fee2e2', color: '#991b1b', fontSize: '12px', flexShrink: 0 }}>
            {error}<button onClick={() => setError('')} style={{ marginLeft: '8px', background: 'none', border: 'none', cursor: 'pointer' }}>×</button>
          </div>
        )}

        {/* Body */}
        <div style={{ flex: 1, overflow: 'hidden', display: 'flex' }}>

          {/* Products tab */}
          {tab === 'products' && (
            <>
              <div style={{ flex: 1, overflowY: 'auto', padding: '12px', borderRight: editProduct !== null ? '1px solid var(--border)' : 'none' }}>
                {loading ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>Загрузка...</div>
                ) : products.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '60px 40px' }}>
                    <div style={{ fontSize: '40px', marginBottom: '12px' }}>🛍</div>
                    <div style={{ fontSize: '14px', color: 'var(--text-muted)' }}>Нет товаров. Добавьте первый!</div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {products.map(p => (
                      <div
                        key={p.id}
                        onClick={() => setEditProduct({ ...p })}
                        style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 14px', background: editProduct?.id === p.id ? 'rgba(91,106,240,0.06)' : 'var(--bg-2)', border: `1px solid ${editProduct?.id === p.id ? 'var(--accent)' : 'var(--border)'}`, borderRadius: '10px', cursor: 'pointer' }}
                      >
                        {p.images?.[0] && <img src={String(p.images[0])} alt={p.name} style={{ width: '44px', height: '44px', borderRadius: '6px', objectFit: 'cover', flexShrink: 0 }} />}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
                          <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                            {Number(p.price).toLocaleString('ru')} ₽ · {p.isDigital ? '📥 Цифровой' : `${p.stock} шт.`} · <code>{p.slug}</code>
                          </div>
                        </div>
                        <div style={{ fontSize: '10px', fontWeight: 700, padding: '2px 7px', borderRadius: '10px', background: p.published ? '#dcfce7' : 'var(--bg-3)', color: p.published ? '#15803d' : 'var(--text-muted)', flexShrink: 0 }}>
                          {p.published ? '✓ Виден' : 'Скрыт'}
                        </div>
                        <button
                          onClick={e => { e.stopPropagation(); deleteProduct(p.id) }}
                          style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: '14px', padding: '4px 6px', opacity: 0.7 }}
                        >🗑</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Product editor */}
              {editProduct !== null && (
                <div style={{ width: '320px', overflowY: 'auto', padding: '16px', flexShrink: 0 }}>
                  <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '14px' }}>
                    {editProduct.id ? 'Редактировать товар' : 'Новый товар'}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {([
                      ['name', 'Название', 'text'],
                      ['slug', 'Slug', 'text'],
                      ['description', 'Описание', 'text'],
                      ['price', 'Цена (₽)', 'number'],
                      ['compareAt', 'Зачёркнутая цена', 'number'],
                      ['stock', 'Остаток (шт.)', 'number'],
                      ['category', 'Категория', 'text'],
                    ] as Array<[keyof ApiProduct, string, string]>).map(([field, label, type]) => (
                      <div key={field}>
                        <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>{label}</label>
                        <input
                          type={type}
                          className="prop-input"
                          value={String(editProduct[field] ?? '')}
                          onChange={e => setEditProduct(p => ({ ...p, [field]: type === 'number' ? Number(e.target.value) : e.target.value }))}
                          min={type === 'number' ? 0 : undefined}
                          step={field === 'price' || field === 'compareAt' ? '0.01' : undefined}
                        />
                      </div>
                    ))}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="checkbox"
                        id="pub-check"
                        checked={!!editProduct.published}
                        onChange={e => setEditProduct(p => ({ ...p, published: e.target.checked }))}
                      />
                      <label htmlFor="pub-check" style={{ fontSize: '13px', cursor: 'pointer', color: 'var(--text-primary)' }}>Опубликован (виден покупателям)</label>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="checkbox"
                        id="digital-check"
                        checked={!!editProduct.isDigital}
                        onChange={e => setEditProduct(p => ({ ...p, isDigital: e.target.checked }))}
                      />
                      <label htmlFor="digital-check" style={{ fontSize: '13px', cursor: 'pointer', color: 'var(--text-primary)' }}>Цифровой товар (файл)</label>
                    </div>
                    {editProduct.isDigital && (
                      <div>
                        <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Ссылка на файл</label>
                        <input
                          type="url"
                          className="prop-input"
                          placeholder="https://..."
                          value={String(editProduct.digitalFileUrl ?? '')}
                          onChange={e => setEditProduct(p => ({ ...p, digitalFileUrl: e.target.value }))}
                        />
                        <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '3px' }}>Покупатель получит ссылку по email после заказа</div>
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                      <button onClick={saveProduct} disabled={saving} style={{ flex: 1, padding: '8px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' }}>
                        {saving ? '...' : 'Сохранить'}
                      </button>
                      <button onClick={() => setEditProduct(null)} style={{ padding: '8px 12px', background: 'var(--bg-3)', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '13px', color: 'var(--text-muted)', cursor: 'pointer' }}>
                        Закрыть
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Promo codes tab */}
          {tab === 'promos' && (
            <>
              <div style={{ flex: 1, overflowY: 'auto', padding: '12px', borderRight: editPromo !== null ? '1px solid var(--border)' : 'none' }}>
                {loading ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>Загрузка...</div>
                ) : promos.length === 0 && !editPromo ? (
                  <div style={{ textAlign: 'center', padding: '60px 40px' }}>
                    <div style={{ fontSize: '40px', marginBottom: '12px' }}>🎟</div>
                    <div style={{ fontSize: '14px', color: 'var(--text-muted)' }}>Нет промокодов. Создайте первый!</div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {promos.map(p => (
                      <div
                        key={p.id}
                        onClick={() => setEditPromo({ id: p.id, code: p.code, type: p.type, value: p.value, minOrder: p.minOrder ?? '', usageLimit: p.usageLimit ? String(p.usageLimit) : '', expiresAt: p.expiresAt ? p.expiresAt.slice(0, 10) : '' })}
                        style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 14px', background: editPromo?.id === p.id ? 'rgba(91,106,240,0.06)' : 'var(--bg-2)', border: `1px solid ${editPromo?.id === p.id ? 'var(--accent)' : 'var(--border)'}`, borderRadius: '10px', cursor: 'pointer' }}
                      >
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <code style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '1px' }}>{p.code}</code>
                            <span style={{ fontSize: '12px', fontWeight: 700, color: '#5b6af0' }}>
                              {p.type === 'PERCENT' ? `${p.value}%` : `${Number(p.value).toLocaleString('ru')} ₽`}
                            </span>
                          </div>
                          <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
                            Использовано: {p.usedCount}{p.usageLimit ? ` / ${p.usageLimit}` : ''}
                            {p.minOrder && ` · Мин. заказ: ${Number(p.minOrder).toLocaleString('ru')} ₽`}
                            {p.expiresAt && ` · до ${new Date(p.expiresAt).toLocaleDateString('ru')}`}
                          </div>
                        </div>
                        <button
                          onClick={e => { e.stopPropagation(); togglePromo(p.id, !p.active) }}
                          style={{ padding: '3px 8px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '11px', fontWeight: 700, background: p.active ? '#dcfce7' : 'var(--bg-3)', color: p.active ? '#15803d' : 'var(--text-muted)', flexShrink: 0 }}
                        >{p.active ? 'Активен' : 'Выкл'}</button>
                        <button
                          onClick={e => { e.stopPropagation(); deletePromo(p.id) }}
                          style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: '14px', padding: '4px 6px', opacity: 0.7 }}
                        >🗑</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Promo editor */}
              {editPromo !== null && (
                <div style={{ width: '300px', overflowY: 'auto', padding: '16px', flexShrink: 0 }}>
                  <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '14px' }}>
                    {editPromo.id ? 'Промокод' : 'Новый промокод'}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {!editPromo.id && (
                      <div>
                        <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Код</label>
                        <input
                          className="prop-input"
                          value={editPromo.code}
                          onChange={e => setEditPromo(p => p && ({ ...p, code: e.target.value.toUpperCase() }))}
                          placeholder="SUMMER20"
                          style={{ textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 700 }}
                        />
                      </div>
                    )}
                    {!editPromo.id && (
                      <div>
                        <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Тип</label>
                        <select
                          className="prop-input"
                          value={editPromo.type}
                          onChange={e => setEditPromo(p => p && ({ ...p, type: e.target.value as 'PERCENT' | 'FIXED' }))}
                        >
                          <option value="PERCENT">Скидка %</option>
                          <option value="FIXED">Фикс. скидка ₽</option>
                        </select>
                      </div>
                    )}
                    {!editPromo.id && (
                      <div>
                        <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
                          {editPromo.type === 'PERCENT' ? 'Размер скидки (%)' : 'Сумма скидки (₽)'}
                        </label>
                        <input type="number" className="prop-input" value={editPromo.value} min={0}
                          onChange={e => setEditPromo(p => p && ({ ...p, value: e.target.value }))} />
                      </div>
                    )}
                    {!editPromo.id && (
                      <div>
                        <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Мин. сумма заказа (₽)</label>
                        <input type="number" className="prop-input" value={editPromo.minOrder} min={0} placeholder="—"
                          onChange={e => setEditPromo(p => p && ({ ...p, minOrder: e.target.value }))} />
                      </div>
                    )}
                    <div>
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Лимит использований</label>
                      <input type="number" className="prop-input" value={editPromo.usageLimit} min={1} placeholder="∞"
                        onChange={e => setEditPromo(p => p && ({ ...p, usageLimit: e.target.value }))} />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Действует до</label>
                      <input type="date" className="prop-input" value={editPromo.expiresAt}
                        onChange={e => setEditPromo(p => p && ({ ...p, expiresAt: e.target.value }))} />
                    </div>
                    <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                      <button onClick={savePromo} disabled={saving} style={{ flex: 1, padding: '8px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' }}>
                        {saving ? '...' : 'Сохранить'}
                      </button>
                      <button onClick={() => setEditPromo(null)} style={{ padding: '8px 12px', background: 'var(--bg-3)', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '13px', color: 'var(--text-muted)', cursor: 'pointer' }}>
                        Закрыть
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Orders tab */}
          {tab === 'orders' && (
            <>
              <div style={{ flex: 1, overflowY: 'auto', borderRight: selectedOrder ? '1px solid var(--border)' : 'none' }}>
                {/* Filter bar */}
                <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)', display: 'flex', gap: '4px', flexWrap: 'wrap', alignItems: 'center' }}>
                  {(['ALL', 'PENDING', 'CONFIRMED', 'PROCESSING', 'SHIPPED', 'DELIVERED', 'CANCELLED'] as OrderStatus[]).map(s => (
                    <button
                      key={s}
                      onClick={() => setOrderFilter(s)}
                      style={{ padding: '3px 10px', borderRadius: '10px', border: 'none', cursor: 'pointer', fontSize: '11px', fontWeight: 600, background: orderFilter === s ? 'var(--accent)' : 'var(--bg-2)', color: orderFilter === s ? '#fff' : 'var(--text-muted)' }}
                    >
                      {s === 'ALL' ? 'Все' : ORDER_STATUS_LABELS[s]?.label ?? s}
                    </button>
                  ))}
                  <button
                    onClick={exportOrdersCsv}
                    style={{ marginLeft: 'auto', padding: '3px 10px', borderRadius: '10px', border: '1px solid var(--border)', background: 'var(--bg-2)', color: 'var(--text-muted)', fontSize: '11px', cursor: 'pointer' }}
                  >↓ CSV</button>
                </div>

                {loading ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>Загрузка...</div>
                ) : orders.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '60px 40px' }}>
                    <div style={{ fontSize: '40px', marginBottom: '12px' }}>📦</div>
                    <div style={{ fontSize: '14px', color: 'var(--text-muted)' }}>Нет заказов</div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    {orders.map(o => {
                      const st = ORDER_STATUS_LABELS[o.status]
                      return (
                        <div
                          key={o.id}
                          onClick={() => setSelectedOrder(x => x?.id === o.id ? null : o)}
                          style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid var(--border)', background: selectedOrder?.id === o.id ? 'rgba(91,106,240,0.06)' : 'transparent' }}
                        >
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)' }}>
                              #{o.number} {o.customerName && `· ${o.customerName}`}
                            </div>
                            <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '1px' }}>
                              {new Date(o.createdAt).toLocaleDateString('ru', { day: '2-digit', month: '2-digit', year: '2-digit' })} · {o.items.length} поз.
                            </div>
                          </div>
                          <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-primary)' }}>
                            {Number(o.total).toLocaleString('ru')} ₽
                          </div>
                          <div style={{ fontSize: '10px', fontWeight: 700, padding: '2px 8px', borderRadius: '10px', background: `${st?.color}22`, color: st?.color, whiteSpace: 'nowrap' }}>
                            {st?.label ?? o.status}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>

              {/* Order detail */}
              {selectedOrder && (
                <div style={{ width: '300px', overflowY: 'auto', padding: '16px', flexShrink: 0 }}>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '12px' }}>
                    Заказ #{selectedOrder.number}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '16px' }}>
                    {selectedOrder.customerName && <InfoRow label="Имя" value={selectedOrder.customerName} />}
                    {selectedOrder.customerEmail && <InfoRow label="Email" value={selectedOrder.customerEmail} />}
                    {selectedOrder.customerPhone && <InfoRow label="Телефон" value={selectedOrder.customerPhone} />}
                  </div>
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>Состав</div>
                    {selectedOrder.items.map(item => (
                      <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', padding: '4px 0', borderBottom: '1px solid var(--border)' }}>
                        <span>{item.name} × {item.quantity}</span>
                        <span style={{ color: 'var(--text-muted)' }}>{(Number(item.price) * item.quantity).toLocaleString('ru')} ₽</span>
                      </div>
                    ))}
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', fontWeight: 700, paddingTop: '8px' }}>
                      <span>Итого</span>
                      <span>{Number(selectedOrder.total).toLocaleString('ru')} ₽</span>
                    </div>
                  </div>
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>Статус</div>
                    <select
                      value={selectedOrder.status}
                      onChange={e => updateStatus(selectedOrder.id, e.target.value)}
                      style={{ width: '100%', padding: '8px 10px', background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '13px', color: 'var(--text-primary)', cursor: 'pointer' }}
                    >
                      {Object.entries(ORDER_STATUS_LABELS).map(([k, v]) => (
                        <option key={k} value={k}>{v.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
            </>
          )}
          {/* Shipping zones tab */}
          {tab === 'shipping' && (
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
              {loading ? (
                <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>Загрузка...</div>
              ) : (
                <>
                  {shippingZones.length === 0 && !addingZone && (
                    <div style={{ textAlign: 'center', padding: '48px 40px' }}>
                      <div style={{ fontSize: '40px', marginBottom: '12px' }}>🚚</div>
                      <div style={{ fontSize: '14px', color: 'var(--text-muted)', marginBottom: '6px' }}>Зоны доставки не настроены</div>
                      <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Добавьте зоны для расчёта стоимости доставки</div>
                    </div>
                  )}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: addingZone ? '16px' : '0' }}>
                    {shippingZones.map(z => (
                      <div key={z.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: '10px' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)' }}>{z.name}</div>
                          <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>
                            {Number(z.flatRate) === 0 ? 'Бесплатно' : `${Number(z.flatRate).toLocaleString('ru')} ₽`}
                            {z.freeAbove && ` · бесплатно от ${Number(z.freeAbove).toLocaleString('ru')} ₽`}
                            {(z.regions as unknown as string[]).length > 0 && ` · ${(z.regions as unknown as string[]).join(', ')}`}
                          </div>
                        </div>
                        <button
                          onClick={async () => {
                            if (!siteId) return
                            await shippingApi.update(siteId, z.id, { active: !z.active }).catch(() => null)
                            setShippingZones(prev => prev.map(s => s.id === z.id ? { ...s, active: !z.active } : s))
                          }}
                          style={{ padding: '3px 8px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '11px', fontWeight: 700, background: z.active ? '#dcfce7' : 'var(--bg-3)', color: z.active ? '#15803d' : 'var(--text-muted)' }}
                        >{z.active ? 'Активна' : 'Выкл'}</button>
                        <button
                          onClick={async () => {
                            if (!siteId || !confirm('Удалить зону доставки?')) return
                            await shippingApi.delete(siteId, z.id).catch(() => null)
                            setShippingZones(prev => prev.filter(s => s.id !== z.id))
                          }}
                          style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: '14px', padding: '4px 6px' }}
                        >🗑</button>
                      </div>
                    ))}
                  </div>
                  {addingZone && (
                    <div style={{ background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: '12px', padding: '16px' }}>
                      <div style={{ fontSize: '13px', fontWeight: 700, marginBottom: '12px', color: 'var(--text-primary)' }}>Новая зона доставки</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <div>
                          <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>Название зоны</label>
                          <input className="prop-input" placeholder="Например: По Москве" value={newZone.name} onChange={e => setNewZone(p => ({ ...p, name: e.target.value }))} />
                        </div>
                        <div>
                          <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>Регионы (через запятую, пусто = все)</label>
                          <input className="prop-input" placeholder="Москва, Московская обл." value={newZone.regions} onChange={e => setNewZone(p => ({ ...p, regions: e.target.value }))} />
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <div style={{ flex: 1 }}>
                            <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>Стоимость (₽, 0 = бесплатно)</label>
                            <input className="prop-input" type="number" min="0" value={newZone.flatRate} onChange={e => setNewZone(p => ({ ...p, flatRate: e.target.value }))} />
                          </div>
                          <div style={{ flex: 1 }}>
                            <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>Бесплатно от суммы (₽)</label>
                            <input className="prop-input" type="number" min="0" placeholder="Не задано" value={newZone.freeAbove} onChange={e => setNewZone(p => ({ ...p, freeAbove: e.target.value }))} />
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                          <button onClick={() => { setAddingZone(false); setNewZone({ name: '', regions: '', flatRate: '0', freeAbove: '' }) }} style={{ padding: '7px 16px', border: '1px solid var(--border)', background: 'var(--bg-3)', color: 'var(--text-secondary)', borderRadius: '8px', fontSize: '12px', cursor: 'pointer' }}>Отмена</button>
                          <button
                            disabled={!newZone.name.trim() || saving}
                            onClick={async () => {
                              if (!siteId || !newZone.name.trim()) return
                              setSaving(true)
                              try {
                                const regions = newZone.regions ? newZone.regions.split(',').map(s => s.trim()).filter(Boolean) : []
                                const z = await shippingApi.create(siteId, { name: newZone.name, regions, flatRate: Number(newZone.flatRate) || 0, freeAbove: newZone.freeAbove ? Number(newZone.freeAbove) : undefined })
                                setShippingZones(prev => [...prev, z])
                                setAddingZone(false)
                                setNewZone({ name: '', regions: '', flatRate: '0', freeAbove: '' })
                              } catch (e) { setError(String(e)) }
                              finally { setSaving(false) }
                            }}
                            style={{ padding: '7px 16px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', opacity: !newZone.name.trim() || saving ? 0.6 : 1 }}
                          >{saving ? '...' : 'Сохранить'}</button>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div style={{ fontSize: '10px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '2px' }}>{label}</div>
      <div style={{ fontSize: '13px', color: 'var(--text-primary)' }}>{value}</div>
    </div>
  )
}
